<template>
  <v-container fluid justify-start class="pt-0">
  
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredItems"
      item-key="mediaItemId"
      :search="search"
      :custom-filter="customFilter"
      :loading="isLoading"
      show-select
      :no-data-text="noDataText"
      :sort-by.sync="sortBy"
      :sort-desc.sync="descending"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{ 
        itemsPerPageOptions: itemsPerPageOptions,
         'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor" class="mt-3">

          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title :class="titleClass">{{ toolbarTitle }}</v-toolbar-title>

          <v-spacer />
          
          <v-toolbar-items>

          <!-- Delete Button -->
          <v-btn
            class="elevation-0"
            :color="appColor_DeleteButton"
            :disabled="disableDelete"
            @click="remove"
            >
            <v-icon>delete</v-icon>
          </v-btn>

          <!-- Add Button -->
          <v-btn :small="isSmallDevice" @click="chooseContentType" :disabled="disableAdd" :color="toolbarColorDark" dark class="elevation-0 mt-0 ml-2" :width="addButtonWidth">
            <span v-if="!isSmallDevice">{{ getExpression('Button', 'AddMedia') }}</span>
            <v-icon v-else>add</v-icon>
          </v-btn>
          
          </v-toolbar-items>
        </v-toolbar>

        <!-- Alert - Why Certain Items Can't Be Selected -->
        <v-row v-if="showSelectableAlert" justify="center" class="mt-2 mx-2">
          <v-alert
            type="info"
            border="left"
            color="info"
            colored-border
            class="mb-0 pa-1 px-3 grey lighten-3"
            >
            <span class="body-2" v-html="getExpression('Alert', 'NotSelectableForDeletion')"></span>
          </v-alert>
        </v-row>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
        
          <!-- Kiosk/Container -->
          <template>
            <v-col v-if="showKiosk" cols="12" sm="3">
              <kiosk-combobox
                v-model="kioskId"
                :options="kiosks"
                :label="kioskComboLabel"
                :placeholder="kioskComboPlaceholder"
                :disabled="disableKiosk"
                class="mr-3"
              />
            </v-col>
          </template>

          <!-- Container -->
          <v-col v-if="showContainer" cols="12" sm="3">
            <v-select
              v-model="containerName"
              :items="containerItems"
              item-value="name"
              item-text="displayName"
              :label="containerLabel"
              :placeholder="containerPlaceholder"
              :disabled="disableContainer"
              class="mr-3"
            />
          </v-col>

          <!-- Media Type -->
          <v-col cols="12" md="2">
            <v-select
              v-model="mediaType"
              :items="mediaTypes"
              item-value="value"
              item-text="text"
              :label="getExpression('Label', 'ContentType')"
              placeholder="Select type"
              :disabled="disableMediaType"
              class="mr-3"
              >
            </v-select>
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" :md="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="disableSearch"
              clearable
              selectable-key="messageId"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.contentType="{ item }">
        <span class="text-left pl-0">
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'image'" large>photo</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'video'" large>movie</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'audio'" large>audiotrack</v-icon>
        </span>
      </template>
      
      <template v-slot:item.thumbnail="{ item }">
        <router-link :to="{name: currentRoute}" style="text-decoration: none">
          <span @click="preview(item)" class="text-center">
            <v-img v-if="getMediaType(item.contentType) !== 'audio'" width="50" height="50" :src="getThumbnailUrl(item)" alt=""></v-img>
            <v-icon v-else large color="blue darken-2">volume_up</v-icon>
          </span>
        </router-link>
      </template>

      <template v-slot:item.title="{ item }">
        <span v-if="item.isEditable && canEditTitle(item)" class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.title }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
        <span v-else>{{ item.title }}</span>
      </template>

      <template v-slot:item.kioskFolder="{ item }">
        <span class="text-left">{{ getBehaviourDescription(item.kioskFolder) }}</span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
      </template>
    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog v-model="previewDialog" max-width="800px" persistent>
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-on:close="closePreview"
       />
    </v-dialog>

    <!-- Content Type Selector -->
    <v-dialog v-model="contentTypeSelectorDialog" :max-width="contentTypeSelectorWidth" persistent>
      <content-type-selector
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-bind:label="mediaTypeSelectorLabel"
        v-bind:showLibrary="false"
        v-bind:disabledTypes="disabledContentTypes"
        v-on:select="uploadMedia"
        v-on:cancel="contentTypeSelectorDialog = false"
      />
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import MediaPreviewer from '@/components/MediaPreviewer'
import ContentTypeSelector from '@/components/ContentTypeSelector'
import { mapGetters } from 'vuex'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import KioskCombobox from '@/components/KioskCombobox'
import { AppColor, AppStyle, Container, KioskFolder, KioskTypeId, ThumbnailUrl, ImageUri, Context, GenieMediaSourceId, UserRole, Base64Image, LanguageId } from '@/config.js'

export default {
  components: {
    MediaPreviewer,
    ContentTypeSelector,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  name: 'MediaManagerr',
  props: {
    sourceId: {
      type: Number,
      default: null
    },
    title: {
      type: String,
      default: null,
    },
    kiosks: {
      type: Array,
      default: null,
    },
    defaultKioskId: {
      type: Number,
      default: null,
    },
    containers: {
      type: Array,
      default: null,
    },
    defaultContainer: {
      type: String,
      default: null,
    },
    isAdminMode: {
      type: Boolean,
      default: false,
    },
    disabledContentTypes: {
      type: Array,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,

      // containers
      container_Public: Container.Public,
      container_Manuals: Container.Manuals,
      container_Promotional: Container.Promotional,

      // kiosk
      kioskPrefix: Container.KioskPrefix,
      kioskFolder_Ambient: KioskFolder.Ambient,
      kioskFolder_Announcement: KioskFolder.Announcement,
      kioskFolder_Email: KioskFolder.Email,

      // resident
      residentPrefix: Container.ResidentPrefix,

      // uri
      thumbnailUrl_Genie: ThumbnailUrl.Genie,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // media sources
      sourceId_Resident: GenieMediaSourceId.Resident,
      sourceId_Kiosk: GenieMediaSourceId.Kiosk,

      // kiosk types
      kioskTypeId_Genie: KioskTypeId.Genie,

      // context (for the media previewer which is shared with abby)
      context_Genie: Context.Genie,

      // image uri
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // roles
      role_Manager: UserRole.Manager,

      kioskId: null,
      containerName: null,
      residentContainers: [],
      selected: [],
      mediaItems: [],
      mediaType: 'all',

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      search: '',
      itemsPerPageOptions: [10, 15, 25, 100],
      sortBy: 'createDate',
      descending: true,

      contentTypeSelectorDialog: false,
      previewDialog: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null,

      editedIndex: -1,
      editedMediaItem: {
        blobName: null,
        title: null,
        kioskFolder: null,
        description: null,
        contentType: null,
        size: null,
        uri: null,
        rotate: null,
        duration: null,
        kioskId: null,
        previewUrl: null
      }
    }
  },
  computed: {
    ...mapGetters(['profile',
      'activeKiosk',
      'isAdmin',
      'isResidentManager',
      'currentRoute',
      'locale'
    ]),
    noDataText () {
      const self = this

      if (self.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (self.containerItems.length === 0) {
        return this.getExpression('Alert', 'NoMediaContainers')
      }
      return self.mediaItems.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    toolbarTitle () {
      return this.title ?? 'Media Libraries'
    },
    filteredItems () {
      const self = this
      const mediaType = self.mediaType

      if (mediaType !== 'all') {
        return self.mediaItems.filter(i => {
          return (
            self.getMediaType(i.contentType) === mediaType ||
            mediaType === 'all'
          )
        })
      } else {
        return self.mediaItems
      }
    },
    containerItems () {
      return this.residentContainers.length > 0
        ? this.residentContainers
        : this.containers
    },
    headers () {
      const self = this
      var headers = []

      headers.push({
        value: 'mediaItemId',
        align: ' d-none'
      })

      headers.push({
        text: self.getExpression('Label', 'Preview'),
        value: 'thumbnail',
        align: 'left',
        width: '3%',
        sortable: false,
        class: 'pr-0'
      })

      headers.push({
        value: 'contentType',
        align: 'left',
        width: '3%',
        class: 'pr-0'
      })

      headers.push({
        text: self.getExpression('Label', 'Title'),
        value: 'title',
        align: 'left',
        width: '20%',
        class: 'pr-0'
      })

      headers.push({
        text: self.getExpression('Label', 'Description'),
        value: 'description',
        align: 'left',
        width: self.isKiosk
            ? '25%'
            : '35%',
        class: 'pr-0'
      })

      if (self.isKiosk) {
        headers.push({
          text: self.getExpression('Label', 'Behaviour'),
          value: 'kioskFolder',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        })
      }

      headers.push({
        text: self.getExpression('Label', 'AddedBy'),
        value: 'addedBy',
        align: 'left',
        width: '20%',
        class: 'pr-0'
      })

      headers.push({
        text: self.getExpression('Label', 'Added'),
        value: 'createDate',
        align: 'left',
        width: '15%',
        class: 'pr-0'
      })

      return headers
    },
    mediaTypes () {
      return [
        {
          value: 'all',
          text: this.getExpression('MenuItem', 'All')
        },
        {
          value: 'audio',
          text: this.getExpression('MenuItem', 'Audio')
        },
        {
          value: 'image',
          text: this.getExpression('MenuItem', 'Image')
        },
        {
          value: 'video',
          text: this.getExpression('MenuItem', 'Video')
        }
      ]
    },
    mediaTypeSelectorLabel () {
      var label = this.getExpression('Label', 'Media')
      return this.profile.languageId === LanguageId.English
        ? label
        : label.toLowerCase()
    },
    kioskComboLabel () {
      return this.isKiosk
        ? this.getExpression('Label', 'Library')
        : this.getExpression('Label', 'Kiosk')
    },
    kioskComboPlaceholder () {
      return this.isKiosk
        ? this.getExpression('Label', 'SelectLibrary')
        : this.getExpression('Label', 'SelectKiosk')
    },
    isResident () {
      return this.sourceId === this.sourceId_Resident
    },
    isKiosk () {
      return this.sourceId === this.sourceId_Kiosk
    },
    containerLabel () {
      return this.isKiosk
        ? this.getExpression('Label','Folder')
        : this.getExpression('Label','Library')
    },
    containerPlaceholder () {
      return this.isKiosk
        ? this.getExpression('Label','SelectFolder')
        : this.getExpression('Label','SelectLibrary')
    },
    selectedContainerName () {
      const name = this.containerName ?? ''
      return name.length > 0
        ? this.containerName
        : this.selectedKiosk !== null
          ? this.selectedKiosk.container
          : null
    },
    selectedKiosk () {
      if (this.kiosks === null || this.kioskId === null) return null

      const self = this
      const kiosks = self.kiosks.filter(k => k.id === self.kioskId)

      return kiosks.length > 0
        ? kiosks[0]
        : null
    },
    disabledCount () {
      return this.filteredItems.filter(m => !m.isSelectable).length
    },
    kioskContainer () {
      if (this.activeKiosk === null) return null
      return this.activeKiosk.container
    },
    disableAdd () {
      const kioskId = this.kioskId ?? 0
      return this.isLoading || (this.containerName === null && kioskId === 0)
    },
    disableDelete () {
      return this.selected.length === 0
    },
    disableMediaType () {
      if (this.isKiosk) {
        const kioskId = this.kioskId ?? 0
        return kioskId === 0 || this.isLoading
      } else {
        const container = this.containerName ?? ''
        return container.length === 0 || this.isLoading
      }
    },
    disableSearch () {
      if (this.isKiosk) {
        const kioskId = this.kioskId ?? 0
        return kioskId === 0 || this.isLoading
      } else {
        const container = this.containerName ?? ''
        return container.length === 0 || this.isLoading
      }
    },
    showSelectableAlert () {
      return this.isResident && this.disabledCount > 0 && !this.isAdminMode
    },
    showContainer () {
      return this.isResident && this.isAdminMode
    },
    disableContainer () {
      if (this.containerItems === null) return true
      return this.containerItems.length <= 1 || this.isLoading
    },
    showKiosk () {
      return this.isAdminMode
    },
    disableKiosk () {
      if (this.isResidentManager) return true
      return this.kiosks.length <= 1 || this.isLoading || this.isResidentManager
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKiosk
        ? this.showContainer
          ? '4'
          : '7'
        : this.showContainer
          ? '7'
          : '10'
    },    
    contentTypeSelectorWidth () {
      return this.profile.languageId === LanguageId.English
        ? '220px'
        : '290px'
    },
    addButtonWidth () {
      return this.profile.languageId === LanguageId.English
        ? this.isSmallDevice ? '' : '160'
        : this.isSmallDevice ? '' : '200'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      this.containerName = this.defaultContainer
      this.kioskId = this.defaultKioskId
    },

    loadMediaItems () {
      const self = this
      const includeSystem = self.isAdmin ? true : null

      self.isLoading = true
      const container = self.selectedContainerName
      appService.getMediaItems(container, null, self.isKiosk, null, includeSystem).then(data => {
        if (data.errorSeverity > 0) self.handleError(data)
        else self.mediaItems = data.mediaItems

        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadResidentContainers () {
      const self = this
      appService.getKioskResidents(self.kioskId).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.residentContainers = data.residents.map(r => { return {
            name: r.container,
            displayName: r.name
          }})
          self.containerName = ''
        }
      })
    },

    remove () {
      const self = this

      self.$media.remove({
        context: self.context_Genie,
        deleteIds: self.selected.map(item => item.mediaItemId),
        onConfirm: () => { self.isDeleting = true },
        onError: (data) => {
          self.isDeleting = false
          self.selected = []
          self.handleError(data)
        },
        onRemove: (mediaItems) => {
          mediaItems.forEach(mediaItem => {
            const deletedMediaItems = self.filteredItems.filter(item => {
              return item.mediaItemId === mediaItem.mediaItemId
            })
            if (deletedMediaItems.length > 0) {
              const indexDeleted = self.mediaItems.indexOf(deletedMediaItems[0])
              self.mediaItems.splice(indexDeleted, 1)
            }
          })
          self.isDeleting = false
          self.selected = []
        }
      })
    },

    edit (item) {
      const self = this

      self.editedIndex = self.mediaItems.indexOf(item)
      self.editedMediaItem = Object.assign({}, item)

      const mediaType = self.getMediaType(item.contentType)
      self.editedMediaItem.previewUrl = mediaType !== 'audio'
        ? item.thumbnail
        : self.imageUri_AudioVideoPlaceholder

      self.$media.edit({
        context: self.context_Genie,
        blob: self.editedMediaItem,
        kioskId: self.kioskId,
        container: self.isKiosk
          ? self.kioskContainer
          : self.containerName,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (mediaItem) => {
          self.mediaItems[self.editedIndex].title = mediaItem.title
          self.mediaItems[self.editedIndex].description = mediaItem.description

          if (self.isKiosk) {
            self.mediaItems[self.editedIndex].kioskFolder = mediaItem.kioskFolder
            self.mediaItems[self.editedIndex].blobName = mediaItem.blobName
          }
        }
      })
    },

    preview (item) {
      const self = this
      appService.getBlobUrl(item.containerName, item.blobName).then(data => {
        const mediaType = self.getMediaType(item.contentType)
        self.previewUrl = data
        self.previewType = mediaType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },

    chooseContentType () {
      this.contentTypeSelectorDialog = true
    },

    uploadMedia (contentType) {
      const self = this

      self.contentTypeSelectorDialog = false

      self.$media.add({
        contentType: contentType,
        context: self.context_Genie,
        container: self.selectedContainerName,
        kioskId: self.kioskId,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,
        
        onSubmit: (uploadResults) => {
          uploadResults.forEach(result => {
            if (result.success) {
              self.mediaItems.unshift(result.mediaItem)
            }
          })
        }
      })
    },

    getBehaviourDescription (kioskFolder) {
      switch (kioskFolder) {
        case this.kioskFolder_Ambient:
          return this.getExpression('Label', 'Standard')
        case this.kioskFolder_Announcement:
          return this.getExpression('Label', 'Announcement')
        case this.kioskFolder_Email:
          return 'Email'
        default:
          return null
      }
    },
    
    customFilter (value, search, item) {
      const description = item.description ?? ''
      const addedBy = item.addedBy ?? ''
      const behaviour = this.getBehaviourDescription(item.kioskFolder) ?? ''

      return (
        (value != null &&
          search != null &&
          item.title.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1) ||
          description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          addedBy.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          behaviour.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    },

    canEditTitle (item) {
      if (this.isKiosk) {
        const editableFolders = [this.kioskFolder_Ambient, this.kioskFolder_Announcement]
        return editableFolders.includes(item.kioskFolder)
      }
    
      return true
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    containerName () {
      if (this.containerName.length > 0) {
        this.loadMediaItems()
      } else this.mediaItems = []
    },

    defaultKioskId () {
      this.kioskId = this.defaultKioskId
      this.isLoading = false
    },

    kioskId () {
      if (this.showKiosk) {
        this.containerName = null
        this.mediaItems = []
        this.residentContainers = []
        if (this.kioskId > 0) {
          if (this.isKiosk) {
            this.loadMediaItems()
          } else {
            this.loadResidentContainers()
          }
        }
      }
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>
